// Pages/almacenamiento/almacenamientoUtils.ts
//
// Lógica PURA de la página /almacenamiento (sin React, sin red): formato de
// pesos y porcentajes, etiquetas de fecha, armado de filtros desde los atajos
// de limpieza y manejo de la selección entre páginas. Vive aparte para
// poder testearla sin montar la página (ver almacenamientoUtils.test.ts).
import dayjs, { Dayjs } from "dayjs";
import { TZ_CHILE } from "../../utils/fechaChile";
import {
  MAX_IDS_POR_OPERACION,
  type Archivo,
  type DireccionOrden,
  type EstadoFiltro,
  type EstadoPreviews,
  type OrdenArchivos,
  type OrigenFecha,
  type ConsumoArchivoTop,
  type ConsumoPorDia,
  type ParamsArchivos,
  type TipoArchivo,
  type TipoConsumo,
} from "../../services/almacenamiento.api";

// ─── Peso ────────────────────────────────────────────────────────────────────

const UNIDADES = ["B", "KB", "MB", "GB", "TB"] as const;

/**
 * Peso legible con UNA sola convención (base 1024, coma decimal):
 * `0 B`, `512 B`, `1,5 KB`, `12,3 MB`, `120 MB`, `3,2 GB`.
 * Menos decimales mientras más grande el número, sin ceros sobrantes.
 */
export function formatPeso(bytes: number | null | undefined): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  let valor = n;
  let i = 0;
  while (valor >= 1024 && i < UNIDADES.length - 1) {
    valor /= 1024;
    i += 1;
  }
  if (i === 0) return `${Math.round(valor)} B`;
  const decimales = valor >= 100 ? 0 : valor >= 10 ? 1 : 2;
  const texto = String(parseFloat(valor.toFixed(decimales))).replace(".", ",");
  return `${texto} ${UNIDADES[i]}`;
}

export const MB = 1024 * 1024;

// ─── Porcentajes y niveles de uso ────────────────────────────────────────────

/** Porcentaje 0..100 de `parte` sobre `total` (0 si el total no sirve). */
export function porcentaje(parte: number, total: number): number {
  if (!Number.isFinite(parte) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.min(100, Math.max(0, (parte / total) * 100));
}

/** `12,5 %`; un resto positivo pero ínfimo se muestra como `<0,1 %`. */
export function formatPorcentaje(valor: number): string {
  if (!Number.isFinite(valor) || valor <= 0) return "0 %";
  if (valor < 0.1) return "<0,1 %";
  const decimales = valor >= 10 ? 0 : 1;
  return `${parseFloat(valor.toFixed(decimales)).toString().replace(".", ",")} %`;
}

export type NivelUso = "ok" | "atencion" | "alto" | "critico";

/** Umbrales del volumen: 70 % atención, 85 % alto, 95 % crítico. */
export function nivelUso(pct: number): NivelUso {
  if (pct >= 95) return "critico";
  if (pct >= 85) return "alto";
  if (pct >= 70) return "atencion";
  return "ok";
}

export const TEXTO_NIVEL_USO: Record<NivelUso, string> = {
  ok: "Espacio holgado",
  atencion: "Conviene ir limpiando",
  alto: "Espacio escaso: limpia pronto",
  critico: "Espacio crítico: limpia ahora",
};

// ─── Etiquetas ───────────────────────────────────────────────────────────────

export const ETIQUETA_TIPO: Record<TipoArchivo, string> = {
  image: "Foto",
  video: "Video",
  doc: "Documento",
};

export const ETIQUETA_TIPO_PLURAL: Record<TipoArchivo, string> = {
  image: "Fotos",
  video: "Videos",
  doc: "Documentos",
};

/** Etiqueta del tipo de una fila: una versión liviana sin su video no es un «Video» más. */
export const ETIQUETA_VERSION_LIVIANA_SUELTA = "Versión liviana sin video";

export function etiquetaTipoArchivo(
  a: Pick<Archivo, "tipo" | "soloVersionLiviana">,
): string {
  return a.soloVersionLiviana ? ETIQUETA_VERSION_LIVIANA_SUELTA : (ETIQUETA_TIPO[a.tipo] ?? a.tipo);
}

// ─── Desglose del peso (archivo + miniatura + versión liviana) ────────────────

export interface DesglosePeso {
  /** El archivo en sí (0 en una versión liviana suelta: su peso es la copia). */
  archivo: number;
  miniatura: number;
  versionLiviana: number;
  total: number;
}

/**
 * Reparte `pesoBytes` (que ya suma las tres partes) entre el archivo, su miniatura y su
 * versión liviana. Tolera un servidor anterior que no manda `previewBytes`.
 */
export function desglosePeso(
  a: Pick<Archivo, "pesoBytes" | "pesoMiniaturaBytes"> & Partial<Pick<Archivo, "previewBytes">>,
): DesglosePeso {
  const total = Math.max(0, Number(a.pesoBytes) || 0);
  const miniatura = Math.max(0, Number(a.pesoMiniaturaBytes) || 0);
  const versionLiviana = Math.max(0, Number(a.previewBytes) || 0);
  return { archivo: Math.max(0, total - miniatura - versionLiviana), miniatura, versionLiviana, total };
}

/** «archivo 120 MB + miniatura 40 KB + versión liviana 8,2 MB» (solo las partes que existen). */
export function textoDesglosePeso(d: DesglosePeso): string {
  const partes: string[] = [];
  if (d.archivo > 0) partes.push(`archivo ${formatPeso(d.archivo)}`);
  if (d.miniatura > 0) partes.push(`miniatura ${formatPeso(d.miniatura)}`);
  if (d.versionLiviana > 0) partes.push(`versión liviana ${formatPeso(d.versionLiviana)}`);
  return partes.length > 0 ? partes.join(" + ") : "sin peso";
}

/** Ayuda de la columna Peso: dice qué incluye el número, o `undefined` si es solo el archivo. */
export function ayudaPeso(a: Parameters<typeof desglosePeso>[0]): string | undefined {
  const d = desglosePeso(a);
  const extras: string[] = [];
  if (d.miniatura > 0) extras.push(`la miniatura (${formatPeso(d.miniatura)})`);
  if (d.versionLiviana > 0) extras.push(`la versión liviana del video (${formatPeso(d.versionLiviana)})`);
  return extras.length > 0 ? `Incluye ${extras.join(" y ")}` : undefined;
}

// ─── Fechas (siempre hora Chile) ─────────────────────────────────────────────

/** Instante ISO → "DD-MM-YYYY HH:mm" en hora chilena. "—" si no es válido. */
export function formatFechaHoraChile(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = dayjs(iso);
  return d.isValid() ? d.tz(TZ_CHILE).format("DD-MM-YYYY HH:mm") : "—";
}

/** Instante ISO → "DD-MM-YYYY" en hora chilena. "—" si no es válido. */
export function formatDiaChile(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = dayjs(iso);
  return d.isValid() ? d.tz(TZ_CHILE).format("DD-MM-YYYY") : "—";
}

export interface EtiquetaFecha {
  texto: string;
  /** Explicación para el tooltip cuando la fecha no es exacta. */
  explicacion: string | null;
  aproximada: boolean;
}

/**
 * Cómo mostrar la fecha de subida de un archivo:
 *  - `registrada`: la guardó el sistema → fecha y hora exactas.
 *  - `deducida`: sale del prefijo con la hora de subida que lleva el nombre
 *    interno del archivo → "~" + día, con explicación.
 *  - `desconocida`: no hay manera de saberla.
 */
export function etiquetaFecha(
  fecha: string | null | undefined,
  origen: OrigenFecha,
): EtiquetaFecha {
  if (!fecha || origen === "desconocida" || !dayjs(fecha).isValid()) {
    return {
      texto: "Fecha desconocida",
      explicacion:
        "No hay forma de saber cuándo se subió este archivo: no se registró y su nombre interno no la conserva.",
      aproximada: true,
    };
  }
  if (origen === "deducida") {
    return {
      texto: `~${formatDiaChile(fecha)}`,
      explicacion:
        "Fecha estimada a partir del nombre interno del archivo. Los archivos anteriores a esta versión no guardan la fecha exacta de subida (y los que se migraron desde otro servicio muestran la fecha de la migración).",
      aproximada: true,
    };
  }
  return { texto: formatFechaHoraChile(fecha), explicacion: null, aproximada: false };
}

/** Dayjs de un DatePicker → "YYYY-MM-DD" (o undefined). */
export function diaISO(valor: Dayjs | null | undefined): string | undefined {
  return valor && dayjs.isDayjs(valor) && valor.isValid()
    ? valor.format("YYYY-MM-DD")
    : undefined;
}

// ─── Filtros ─────────────────────────────────────────────────────────────────

export interface FiltrosArchivos {
  q?: string;
  modulo?: string;
  tipo?: TipoArchivo;
  propiedadId?: number;
  /** YYYY-MM-DD */
  desde?: string;
  hasta?: string;
  antiguedadMeses?: number;
  pesoMinMB?: number;
  estado: EstadoFiltro;
  orden: OrdenArchivos;
  dir: DireccionOrden;
}

/** Sin filtros: todo, lo más pesado primero (lo útil para limpiar). */
export const FILTROS_BASE: FiltrosArchivos = {
  estado: "todos",
  orden: "peso",
  dir: "desc",
};

export const TIPOS_VALIDOS: TipoArchivo[] = ["image", "video", "doc"];
export const ESTADOS_VALIDOS: EstadoFiltro[] = ["todos", "en_uso", "huerfano", "roto"];
export const ORDENES_VALIDOS: OrdenArchivos[] = ["peso", "fecha", "nombre", "modulo"];
export const ANTIGUEDADES_MESES = [3, 6, 12, 24];
export const PESOS_MIN_MB = [1, 10, 50, 100, 500, 1024];

export const ETIQUETA_ORDEN: Record<OrdenArchivos, string> = {
  peso: "Peso",
  fecha: "Fecha de subida",
  nombre: "Nombre",
  modulo: "Módulo",
};

export const ETIQUETA_ESTADO: Record<EstadoFiltro, string> = {
  todos: "Todos",
  en_uso: "En uso",
  huerfano: "Huérfanos",
  roto: "Referencia rota",
};

/** Texto de un umbral de peso: 1024 MB se lee como 1 GB. */
export function etiquetaPesoMin(mb: number): string {
  return mb >= 1024 ? `${mb / 1024} GB o más` : `${mb} MB o más`;
}

/** Texto de un umbral de antigüedad: 12 → "1 año", 24 → "2 años". */
export function etiquetaAntiguedad(meses: number): string {
  if (meses >= 12 && meses % 12 === 0) {
    const a = meses / 12;
    return `Más de ${a} ${a === 1 ? "año" : "años"}`;
  }
  return `Más de ${meses} meses`;
}

/** Parámetros de la request a partir de los filtros, sin los que no filtran. */
export function construirParams(
  f: FiltrosArchivos,
  page: number,
  size: number,
): ParamsArchivos {
  const q = f.q?.trim();
  return {
    q: q || undefined,
    modulo: f.modulo || undefined,
    tipo: f.tipo || undefined,
    propiedadId: f.propiedadId,
    desde: f.desde || undefined,
    hasta: f.hasta || undefined,
    antiguedadMeses: f.antiguedadMeses,
    pesoMinMB: f.pesoMinMB,
    estado: f.estado && f.estado !== "todos" ? f.estado : undefined,
    orden: f.orden,
    dir: f.dir,
    page,
    size,
  };
}

/**
 * ¿Hay algún filtro puesto? El orden NO cuenta: ordenar no excluye archivos y
 * un "Limpiar filtros" que aparece solo por el orden confunde.
 */
export function hayFiltrosActivos(f: FiltrosArchivos): boolean {
  return Boolean(
    f.q?.trim() ||
      f.modulo ||
      f.tipo ||
      f.propiedadId != null ||
      f.desde ||
      f.hasta ||
      f.antiguedadMeses != null ||
      f.pesoMinMB != null ||
      (f.estado && f.estado !== "todos"),
  );
}

export type IdAtajo = "huerfanos" | "mas6m" | "mas1a" | "videosPesados" | "masPesado";

export interface Atajo {
  id: IdAtajo;
  etiqueta: string;
  descripcion: string;
  filtros: FiltrosArchivos;
}

/**
 * Atajos de limpieza. Cada uno REEMPLAZA todos los filtros (parte de
 * FILTROS_BASE): sumar un atajo a filtros viejos daba resultados que nadie
 * esperaba ("huérfanos" + un módulo que se quedó puesto).
 */
export const ATAJOS: Atajo[] = [
  {
    id: "huerfanos",
    etiqueta: "Huérfanos (sin uso)",
    descripcion:
      "Archivos que ningún registro del sistema usa. Es el espacio que se recupera sin riesgo.",
    filtros: { ...FILTROS_BASE, estado: "huerfano" },
  },
  {
    id: "mas6m",
    etiqueta: "Más de 6 meses",
    descripcion: "Archivos subidos hace más de 6 meses, los más pesados primero.",
    filtros: { ...FILTROS_BASE, antiguedadMeses: 6 },
  },
  {
    id: "mas1a",
    etiqueta: "Más de 1 año",
    descripcion: "Archivos subidos hace más de un año, los más pesados primero.",
    filtros: { ...FILTROS_BASE, antiguedadMeses: 12 },
  },
  {
    id: "videosPesados",
    etiqueta: "Videos pesados (>100 MB)",
    descripcion: "Videos de 100 MB o más: casi siempre donde está el espacio.",
    filtros: { ...FILTROS_BASE, tipo: "video", pesoMinMB: 100 },
  },
  {
    id: "masPesado",
    etiqueta: "Lo más pesado",
    descripcion: "Quita todos los filtros y ordena de mayor a menor peso.",
    filtros: { ...FILTROS_BASE },
  },
];

const CAMPOS_COMPARABLES: Array<keyof FiltrosArchivos> = [
  "q",
  "modulo",
  "tipo",
  "propiedadId",
  "desde",
  "hasta",
  "antiguedadMeses",
  "pesoMinMB",
  "estado",
  "orden",
  "dir",
];

const normalizarCampo = (f: FiltrosArchivos, campo: keyof FiltrosArchivos) => {
  const v = f[campo];
  if (campo === "q") return (typeof v === "string" ? v.trim() : "") || undefined;
  return v === "" || v === null ? undefined : v;
};

export function filtrosIguales(a: FiltrosArchivos, b: FiltrosArchivos): boolean {
  return CAMPOS_COMPARABLES.every((c) => normalizarCampo(a, c) === normalizarCampo(b, c));
}

/** El atajo cuyo resultado coincide EXACTAMENTE con los filtros actuales. */
export function atajoActivo(f: FiltrosArchivos): IdAtajo | null {
  // `masPesado` es "sin filtros": si no hay ninguno, no se destaca ningún chip
  // (resaltarlo siempre lo haría parecer un filtro puesto).
  if (!hayFiltrosActivos(f)) return null;
  const hallado = ATAJOS.find((a) => filtrosIguales(f, a.filtros));
  return hallado ? hallado.id : null;
}

/** Id de un atajo → los filtros que hay que dejar puestos. */
export function filtrosDeAtajo(id: IdAtajo): FiltrosArchivos {
  const atajo = ATAJOS.find((a) => a.id === id);
  return { ...(atajo ? atajo.filtros : FILTROS_BASE) };
}

/**
 * Enlaces directos (`/almacenamiento?modulo=aseos&estado=huerfano`). Solo se
 * aceptan valores que la página sabe manejar; el resto se ignora.
 */
export function parsearEnlaceDirecto(params: URLSearchParams): {
  valores: Partial<FiltrosArchivos>;
  hayEnlace: boolean;
} {
  const valores: Partial<FiltrosArchivos> = {};
  const modulo = params.get("modulo");
  if (modulo && /^[\w.-]{1,60}$/.test(modulo)) valores.modulo = modulo;
  const tipo = params.get("tipo");
  if (tipo && (TIPOS_VALIDOS as string[]).includes(tipo)) valores.tipo = tipo as TipoArchivo;
  const estado = params.get("estado");
  if (estado && (ESTADOS_VALIDOS as string[]).includes(estado)) {
    valores.estado = estado as EstadoFiltro;
  }
  const propiedad = Number(params.get("propiedadId") ?? params.get("propiedad"));
  if (Number.isInteger(propiedad) && propiedad > 0) valores.propiedadId = propiedad;
  const q = params.get("q");
  if (q && q.trim()) valores.q = q.trim().slice(0, 120);
  return { valores, hayEnlace: Object.keys(valores).length > 0 };
}

// ─── Resumen del filtro ──────────────────────────────────────────────────────

export interface ResumenFiltro {
  texto: string;
  porcentaje: number;
  porcentajeTexto: string;
}

/** «N archivos · X GB con estos filtros (12 % del espacio de archivos)». */
export function resumenDelFiltro(
  total: number,
  bytesFiltrados: number,
  bytesTodos: number,
): ResumenFiltro {
  const pct = porcentaje(bytesFiltrados, bytesTodos);
  const archivos = `${total.toLocaleString("es-CL")} ${total === 1 ? "archivo" : "archivos"}`;
  return {
    texto: `${archivos} · ${formatPeso(bytesFiltrados)} con estos filtros`,
    porcentaje: pct,
    porcentajeTexto: formatPorcentaje(pct),
  };
}

// ─── Selección entre páginas ─────────────────────────────────────────────────

export type Seleccion = ReadonlyMap<string, Archivo>;

/**
 * Suma filas a la selección respetando el tope por operación. Devuelve una
 * selección nueva (nunca muta) y cuántas filas quedaron fuera por el tope.
 */
export function agregarASeleccion(
  actual: Seleccion,
  filas: Archivo[],
  max: number = MAX_IDS_POR_OPERACION,
): { seleccion: Map<string, Archivo>; omitidos: number } {
  const siguiente = new Map(actual);
  let omitidos = 0;
  for (const fila of filas) {
    if (siguiente.has(fila.id)) {
      siguiente.set(fila.id, fila); // refresca datos (URL firmada, estado)
      continue;
    }
    if (siguiente.size >= max) {
      omitidos += 1;
      continue;
    }
    siguiente.set(fila.id, fila);
  }
  return { seleccion: siguiente, omitidos };
}

export function quitarDeSeleccion(actual: Seleccion, ids: string[]): Map<string, Archivo> {
  const siguiente = new Map(actual);
  for (const id of ids) siguiente.delete(id);
  return siguiente;
}

export function totalSeleccion(sel: Seleccion): { cantidad: number; bytes: number } {
  let bytes = 0;
  sel.forEach((a) => {
    bytes += Number(a.pesoBytes) || 0;
  });
  return { cantidad: sel.size, bytes };
}

/**
 * Huella de los filtros que definen QUÉ archivos se ven (excluye el orden y la
 * dirección: reordenar la lista no cambia el conjunto). La selección solo es
 * válida mientras esta huella no cambie.
 */
export function huellaConjunto(f: FiltrosArchivos): string {
  return JSON.stringify([
    normalizarCampo(f, "q") ?? null,
    f.modulo ?? null,
    f.tipo ?? null,
    f.propiedadId ?? null,
    f.desde ?? null,
    f.hasta ?? null,
    f.antiguedadMeses ?? null,
    f.pesoMinMB ?? null,
    f.estado,
  ]);
}

/** Como `huellaConjunto`, pero también con el orden (paginar bajo otro orden salta o repite filas). */
export function huellaConOrden(f: FiltrosArchivos): string {
  return `${huellaConjunto(f)}|${f.orden}|${f.dir}`;
}

// ─── Descarga ────────────────────────────────────────────────────────────────

/**
 * URL firmada + `?d=1`: el backend responde `Content-Disposition: attachment`.
 * Sin esto las imágenes, videos y PDF se abren en el navegador y NO se bajan
 * (el atributo `download` lo ignora el navegador en un origen distinto, y el
 * backend es otro servicio). Misma convención que AttachmentsManager.
 */
export function urlDeDescarga(url: string): string {
  return `${url}${url.includes("?") ? "&" : "?"}d=1`;
}

// ─── Confirmación escrita ────────────────────────────────────────────────────

/** Case-insensitive y sin espacios sobrantes: la fricción es intencional, no un test de tipeo. */
export function confirmacionValida(texto: string, requerido: string): boolean {
  return requerido.trim() !== "" && texto.trim().toLowerCase() === requerido.trim().toLowerCase();
}

// ─── Consumo de datos de /files (round 2026-10-27) ───────────────────────────

/** Etiqueta de cada tipo medido (las miniaturas cuentan aparte de las fotos). */
export const ETIQUETA_TIPO_CONSUMO: Record<TipoConsumo, string> = {
  image: "Fotos",
  thumb: "Miniaturas",
  video: "Videos",
  doc: "Documentos",
  preview: "Video (versión liviana)",
};

/** Orden de las barras por tipo: primero lo que suele pesar más; la versión liviana junto al video. */
export const ORDEN_TIPOS_CONSUMO: TipoConsumo[] = ["video", "preview", "image", "doc", "thumb"];

/** Cuántos archivos se listan en «los más servidos». */
export const TOP_CONSUMO_VISIBLE = 10;

const RE_DIA = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `2026-10-27` → `27-10`. "—" si no es un día válido. */
export function formatDiaCorto(fecha: string | null | undefined): string {
  const m = RE_DIA.exec(String(fecha ?? ""));
  return m ? `${m[3]}-${m[2]}` : "—";
}

/** `2026-10-27` → `27-10-2026`. "—" si no es un día válido. */
export function formatDiaDMA(fecha: string | null | undefined): string {
  const m = RE_DIA.exec(String(fecha ?? ""));
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "—";
}

/** «1 petición» / «12 peticiones» (con separador de miles chileno). */
export function formatPeticiones(n: number | null | undefined): string {
  const v = Math.max(0, Math.floor(Number(n) || 0));
  return `${v.toLocaleString("es-CL")} ${v === 1 ? "petición" : "peticiones"}`;
}

export interface BarraDia {
  fecha: string;
  bytes: number;
  /** 0..100, relativo al día de mayor consumo. */
  pct: number;
}

/**
 * Alto relativo de cada barra de la serie diaria. Un día con consumo nunca se dibuja
 * con menos de 4 % (para que se vea que hubo algo); un día sin consumo mide 0.
 */
export function barrasPorDia(porDia: ConsumoPorDia[]): BarraDia[] {
  const max = porDia.reduce((m, d) => Math.max(m, Number(d.bytes) || 0), 0);
  return porDia.map((d) => {
    const bytes = Math.max(0, Number(d.bytes) || 0);
    if (max <= 0 || bytes <= 0) return { fecha: d.fecha, bytes, pct: 0 };
    return { fecha: d.fecha, bytes, pct: Math.max(4, porcentaje(bytes, max)) };
  });
}

/** Los N archivos más servidos (la API ya los manda ordenados; se vuelve a asegurar). */
export function topVisible(top: ConsumoArchivoTop[], n: number = TOP_CONSUMO_VISIBLE): ConsumoArchivoTop[] {
  return [...top].sort((a, b) => b.bytes - a.bytes || b.peticiones - a.peticiones).slice(0, n);
}

/** Nombre a mostrar de un archivo servido: el conocido o, si no, el final de su ruta. */
export function nombreConsumo(a: Pick<ConsumoArchivoTop, "nombre" | "clave">): string {
  const nombre = a.nombre?.trim();
  if (nombre) return nombre;
  const partes = a.clave.split("/");
  return partes[partes.length - 1] || a.clave;
}

// ─── Versiones livianas de los videos (round 2026-10-27) ─────────────────────

/** Cada cuánto se consulta el avance del relleno mientras corre. */
export const SONDEO_PREVIEWS_MS = 5000;

/** Fallos seguidos al consultar el avance tras los cuales se deja de insistir. */
export const MAX_FALLOS_SONDEO_PREVIEWS = 3;

/** Estado de un relleno que nunca corrió (o que el servidor perdió al reiniciarse). */
export const ESTADO_PREVIEWS_VACIO: EstadoPreviews = {
  enCurso: false,
  pendientes: 0,
  generadas: 0,
  fallidas: 0,
  omitidas: 0,
  total: 0,
  iniciadoEn: null,
  terminadoEn: null,
};

/** Completa lo que el servidor no mande (la respuesta de `generar` trae solo conteos). */
export function normalizarEstadoPreviews(
  raw: Partial<EstadoPreviews> | null | undefined,
  respaldo: Partial<EstadoPreviews> = {},
): EstadoPreviews {
  const n = (v: unknown, otro: unknown = 0) => {
    const x = Number(v ?? otro);
    return Number.isFinite(x) && x > 0 ? Math.floor(x) : 0;
  };
  const r = { ...respaldo, ...(raw ?? {}) };
  return {
    enCurso: r.enCurso === true,
    pendientes: n(r.pendientes),
    generadas: n(r.generadas),
    fallidas: n(r.fallidas),
    omitidas: n(r.omitidas),
    total: n(r.total),
    iniciadoEn: typeof r.iniciadoEn === "string" ? r.iniciadoEn : null,
    terminadoEn: typeof r.terminadoEn === "string" ? r.terminadoEn : null,
  };
}

/** Videos ya procesados de la corrida (0..total). */
export function procesadosPreviews(e: EstadoPreviews): number {
  return Math.min(e.total, Math.max(0, e.total - e.pendientes));
}

/** Avance 0..100 de la corrida; 0 si no hay nada que procesar. */
export function porcentajePreviews(e: EstadoPreviews): number {
  return e.total > 0 ? porcentaje(procesadosPreviews(e), e.total) : 0;
}

/** ¿Hay un resultado que mostrar de una corrida que ya terminó? */
export function hayResultadoPreviews(e: EstadoPreviews): boolean {
  return !e.enCurso && e.terminadoEn !== null && e.total > 0;
}

/** «Se crearon 9 versiones livianas; 2 videos no la necesitan; 1 falló.» */
export function textoResultadoPreviews(e: EstadoPreviews): string {
  const plural = (n: number, uno: string, varios: string) => `${n.toLocaleString("es-CL")} ${n === 1 ? uno : varios}`;
  const partes = [`Se ${e.generadas === 1 ? "creó" : "crearon"} ${plural(e.generadas, "versión liviana", "versiones livianas")}`];
  if (e.omitidas > 0) {
    partes.push(
      `${plural(e.omitidas, "video no la necesita", "videos no la necesitan")} (ya son de baja resolución o la copia no ahorra)`,
    );
  }
  if (e.fallidas > 0) {
    partes.push(`${plural(e.fallidas, "video falló", "videos fallaron")} y se verá en su calidad original`);
  }
  return `${partes.join("; ")}.`;
}
